/**
 * Admin page for managing penilaian peer assignments per period
 */

import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import type {
  AssessorRole,
  AssignmentStatus,
  EmployeeOverviewRow,
} from '../types/penilaian.types'

// ─── small chip component ─────────────────────────────────────────────────────

const STATUS_DOT: Record<AssignmentStatus, string> = {
  pending: 'bg-gray-400',
  in_progress: 'bg-yellow-400',
  submitted: 'bg-green-500',
}

const ROLE_CHIP: Record<AssessorRole, string> = {
  ATASAN: 'bg-purple-100 text-purple-700 border border-purple-200',
  PEER: 'bg-blue-100 text-blue-700 border border-blue-200',
  KOMITE: 'bg-orange-100 text-orange-700 border border-orange-200',
  BAWAHAN: 'bg-teal-100 text-teal-700 border border-teal-200',
}

interface ChipProps {
  name: string
  status: AssignmentStatus
  role: AssessorRole
  onDelete: () => void
  disabled: boolean
}

function AssignmentChip({ name, status, role, onDelete, disabled }: ChipProps) {
  return (
    <span
      className={`inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full text-xs font-medium ${ROLE_CHIP[role]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[status]}`} />
      <span className="max-w-[100px] truncate">{name}</span>
      <button
        onClick={onDelete}
        disabled={disabled}
        title="Hapus"
        className="ml-0.5 opacity-50 hover:opacity-100 disabled:opacity-20 leading-none"
      >
        ×
      </button>
    </span>
  )
}

// ─── add-assignment modal ─────────────────────────────────────────────────────

interface AddModalProps {
  role: AssessorRole
  assesseeId: string
  options: { id: string; name: string }[]
  onAdd: (assessorId: string) => void
  onClose: () => void
  loading: boolean
}

function AddModal({ role, assesseeId, options, onAdd, onClose, loading }: AddModalProps) {
  const [selected, setSelected] = useState('')
  const ROLE_LABEL = { ATASAN: 'Atasan', PEER: 'Peer', KOMITE: 'Komite', BAWAHAN: 'Bawahan' }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-80">
        <h3 className="font-semibold text-gray-900 mb-1">Tambah {ROLE_LABEL[role]}</h3>
        <p className="text-xs text-gray-500 mb-4">Pilih karyawan sebagai penilai</p>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">-- Pilih Penilai --</option>
          {options
            .filter((o) => o.id !== assesseeId)
            .map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
        </select>
        <div className="flex gap-3">
          <button
            onClick={() => onAdd(selected)}
            disabled={!selected || loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Menyimpan...' : 'Tambah'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── main page ────────────────────────────────────────────────────────────────

interface AddingState {
  employeeId: string
  role: AssessorRole
}

export default function PeerAssignmentAdminPage() {
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const [periodId, setPeriodId] = useState(searchParams.get('period') ?? '')
  const [addingFor, setAddingFor] = useState<AddingState | null>(null)

  // ── data queries ────────────────────────────────────────────────────────────

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const { data: overviewData, isLoading } = useQuery({
    queryKey: ['penilaian-overview', periodId],
    queryFn: () => penilaianApi.getEmployeeOverview(periodId),
    enabled: !!periodId,
  })

  const rows = overviewData?.data ?? []

  const periods = periodsData?.data ?? []
  const selectedPeriod = periods.find((p) => p.id === periodId)
  const isOpen = selectedPeriod?.status === 'open'

  useEffect(() => {
    if (periodId || periods.length === 0) return
    const open = periods.find((p) => p.status === 'open')
    if (open) { setPeriodId(open.id); return }
    const sorted = [...periods].sort((a, b) =>
      b.year !== a.year ? b.year - a.year : b.quarter - a.quarter
    )
    if (sorted[0]) setPeriodId(sorted[0].id)
  }, [periods, periodId])

  const byDivision = useMemo(() => {
    const groups: Record<string, EmployeeOverviewRow[]> = {}
    for (const row of rows) {
      const div = row.employee.division_name ?? '(Tanpa Divisi)'
      if (!groups[div]) groups[div] = []
      groups[div].push(row)
    }
    return groups
  }, [rows])

  const employeeOptions = useMemo(
    () =>
      rows.map((r) => ({
        id: r.employee.id,
        name:
          r.employee.display_name ||
          r.employee.full_name ||
          r.employee.email,
      })),
    [rows]
  )

  // ── mutations ───────────────────────────────────────────────────────────────

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['penilaian-overview', periodId] })

  const autoAtasanMut = useMutation({
    mutationFn: () => penilaianApi.autoAssignAtasan(periodId),
    onSuccess: (res) => {
      invalidate()
      toast.success(`${res.total} penugasan ATASAN dibuat`)
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal auto-assign ATASAN'),
  })

  const autoBawahanMut = useMutation({
    mutationFn: () => penilaianApi.autoAssignBawahan(periodId),
    onSuccess: (res) => {
      invalidate()
      toast.success(`${res.total} penugasan BAWAHAN dibuat`)
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal auto-assign BAWAHAN'),
  })

const deleteMut = useMutation({
    mutationFn: (id: string) => penilaianApi.deleteAssignment(id),
    onSuccess: () => {
      invalidate()
      toast.success('Penugasan dihapus')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menghapus'),
  })

  const addMut = useMutation({
    mutationFn: ({
      employeeId,
      role,
      assessorId,
    }: {
      employeeId: string
      role: AssessorRole
      assessorId: string
    }) =>
      penilaianApi.addSingleAssignment(periodId, {
        assessor_id: assessorId,
        assessee_id: employeeId,
        assessor_role: role,
      }),
    onSuccess: () => {
      invalidate()
      setAddingFor(null)
      toast.success('Penilai ditambahkan')
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menambah penilai'),
  })

  // ── handlers ────────────────────────────────────────────────────────────────

  const handleDirectAtasan = (employeeId: string, managerId: string) => {
    addMut.mutate({ employeeId, role: 'ATASAN', assessorId: managerId })
  }

  const handleAdd = (assessorId: string) => {
    if (!addingFor || !assessorId) return
    addMut.mutate({ employeeId: addingFor.employeeId, role: addingFor.role, assessorId })
  }

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Penugasan Penilaian</h1>
        <p className="text-sm text-gray-500 mt-1">
          Atur penilai per karyawan untuk setiap periode penilaian
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={periodId}
          onChange={(e) => setPeriodId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[220px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">-- Pilih Periode --</option>
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        {periodId && isOpen && (
          <>
            <button
              onClick={() => autoAtasanMut.mutate()}
              disabled={autoAtasanMut.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-50"
            >
              {autoAtasanMut.isPending ? 'Memproses...' : 'Auto-Assign Semua Atasan'}
            </button>
            <button
              onClick={() => autoBawahanMut.mutate()}
              disabled={autoBawahanMut.isPending}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium disabled:opacity-50"
            >
              {autoBawahanMut.isPending ? 'Memproses...' : 'Auto-Assign Semua Bawahan'}
            </button>
          </>
        )}
      </div>

      {/* Period status notice */}
      {selectedPeriod && !isOpen && (
        <div className="mb-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
          {selectedPeriod.status === 'draft'
            ? <>Periode ini masih <strong>Draft</strong> — penugasan hanya bisa diubah saat periode berstatus <strong>Open</strong>.</>
            : <>Periode ini sudah <strong>Ditutup</strong> — penugasan tidak dapat diubah.</>}
        </div>
      )}

      {/* Legend */}
      {periodId && !isLoading && rows.length > 0 && (
        <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-gray-400" /> Belum dimulai
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-400" /> Sedang berjalan
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Selesai
          </span>
        </div>
      )}

      {/* Content */}
      {!periodId ? (
        <div className="text-center py-20 text-gray-400">
          Pilih periode untuk melihat penugasan
        </div>
      ) : isLoading ? (
        <div className="text-center py-20 text-gray-500">Memuat data karyawan...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20 text-gray-400">Tidak ada data karyawan aktif</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(byDivision)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([division, empRows]) => (
              <div
                key={division}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                {/* Division header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800 text-sm">{division}</span>
                    <span className="text-xs text-gray-400">({empRows.length} karyawan)</span>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-4 py-2 font-medium w-52">Karyawan</th>
                        <th className="text-left px-4 py-2 font-medium w-44">Atasan</th>
                        <th className="text-left px-4 py-2 font-medium">Peer Assessor</th>
                        <th className="text-left px-4 py-2 font-medium w-48">Komite</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {empRows.map(({ employee, assignments }) => {
                        const atasanList = assignments.filter(
                          (a) => a.assessor_role === 'ATASAN'
                        )
                        const peerList = assignments.filter(
                          (a) => a.assessor_role === 'PEER'
                        )
                        const komiteList = assignments.filter(
                          (a) => a.assessor_role === 'KOMITE'
                        )

                        return (
                          <tr key={employee.id} className="hover:bg-gray-50/60 align-top">
                            {/* Employee info */}
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 leading-tight">
                                {employee.display_name ||
                                  employee.full_name ||
                                  employee.email}
                              </div>
                              {employee.job_title && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {employee.job_title}
                                </div>
                              )}
                            </td>

                            {/* ATASAN */}
                            <td className="px-4 py-3">
                              {atasanList.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {atasanList.map((a) => (
                                    <AssignmentChip
                                      key={a.id}
                                      name={a.assessor_name ?? '?'}
                                      status={a.status}
                                      role="ATASAN"
                                      onDelete={() => deleteMut.mutate(a.id)}
                                      disabled={!isOpen || deleteMut.isPending}
                                    />
                                  ))}
                                </div>
                              ) : employee.manager_id ? (
                                <div>
                                  <span className="text-xs text-gray-400 italic">
                                    {employee.manager_name ?? '(ada atasan)'}
                                  </span>
                                  {isOpen && (
                                    <button
                                      onClick={() =>
                                        handleDirectAtasan(employee.id, employee.manager_id!)
                                      }
                                      disabled={addMut.isPending}
                                      className="block text-xs text-purple-600 hover:underline mt-0.5 disabled:opacity-50"
                                    >
                                      + Assign
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>

                            {/* PEER */}
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1 items-center">
                                {peerList.map((a) => (
                                  <AssignmentChip
                                    key={a.id}
                                    name={a.assessor_name ?? '?'}
                                    status={a.status}
                                    role="PEER"
                                    onDelete={() => deleteMut.mutate(a.id)}
                                    disabled={!isOpen || deleteMut.isPending}
                                  />
                                ))}
                                {isOpen && (
                                  <button
                                    onClick={() =>
                                      setAddingFor({ employeeId: employee.id, role: 'PEER' })
                                    }
                                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-dashed border-blue-300 text-blue-400 rounded text-xs hover:bg-blue-50 hover:border-blue-400"
                                    title="Tambah peer assessor"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            </td>

                            {/* KOMITE */}
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1 items-center">
                                {komiteList.map((a) => (
                                  <AssignmentChip
                                    key={a.id}
                                    name={a.assessor_name ?? '?'}
                                    status={a.status}
                                    role="KOMITE"
                                    onDelete={() => deleteMut.mutate(a.id)}
                                    disabled={!isOpen || deleteMut.isPending}
                                  />
                                ))}
                                {isOpen && (
                                  <button
                                    onClick={() =>
                                      setAddingFor({ employeeId: employee.id, role: 'KOMITE' })
                                    }
                                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-dashed border-orange-300 text-orange-400 rounded text-xs hover:bg-orange-50 hover:border-orange-400"
                                    title="Tambah komite"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Add Assignment Modal */}
      {addingFor && (
        <AddModal
          role={addingFor.role}
          assesseeId={addingFor.employeeId}
          options={employeeOptions}
          onAdd={handleAdd}
          onClose={() => setAddingFor(null)}
          loading={addMut.isPending}
        />
      )}
    </div>
  )
}
